import { useEffect, useState } from "react";
import Moves from "./Moves";
import {
  destructureLine,
  getLines,
  getMove,
  getPrevMoveNotation,
  isMoveValid,
  parseMove,
} from "../../util/Chess";
import constants from "../../util/constants";
import { filterInPlace } from "../../util/helpers";
import clsx from "clsx";
import { WHITE } from "chess.js";
const MOVE_TYPES = constants.CHESSBOARD.MOVE_TYPES;

const AddComment = ({
  moves,
  currentMoveIdx,
  setCurrentMoveIdx,
  setMoves,
  handleAddComment,
  updateContentForMove,
  variationToComment = null,
  onVariationCommented = () => { },
  handleDeleteCurrentCommentLine = () => { },
  handleUndoCommentMove = () => { },
  isVariationActive = false,
  isUserAuthenticated = () => true,
  setShowLoginPrompt = () => {},
  onInputFocusChange = null
}) => {
  const [comment, setComment] = useState("");
  const [moveContent, setMoveContent] = useState("");
  const [error, setError] = useState(false);
  const [isVariationComment, setIsVariationComment] = useState(false);
  const [variationTitle, setVariationTitle] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  // NEW: Notify parent when input focus changes
  useEffect(() => {
    if (onInputFocusChange) {
      onInputFocusChange(isInputFocused);
    }
  }, [isInputFocused, onInputFocusChange]);

  // Initialize when a variation is passed for commenting
  useEffect(() => {
    if (variationToComment) {
      setIsVariationComment(true);
      // No longer prefill with suggested title - just leave it blank
      setVariationTitle("");
    } else {
      setIsVariationComment(false);
      setVariationTitle("");
    }
  }, [variationToComment]);

  useEffect(() => {
    const move = getMove(moves, currentMoveIdx);
    if (!move.creator || move.creator.id !== -1) {
      setMoveContent("");
      return;
    }
    setMoveContent(move.content || "");
  }, [currentMoveIdx, moves]);

  const updateMoveContent = () => {
    const move = getMove(moves, currentMoveIdx);
    if (!move.creator || move.creator.id !== -1) {
      alert("Can only add caption for new comments");
      setMoveContent("");
      return;
    }

    updateContentForMove(moveContent);
  };

  const submitComment = async () => {
    setError(false);

    // Validate input
    if (isVariationComment && !variationTitle) {
      setError(true);
      return;
    }

    if (!isVariationComment && !comment) {
      setError(true);
      return;
    }

    // Check if user is authenticated
    if (!isUserAuthenticated()) {
      setShowLoginPrompt(true);
      return;
    }

    let finalComment = isVariationComment
      ? variationTitle
      : comment;

    // Submit the comment
    await handleAddComment(finalComment);

    // Reset form
    setComment("");
    setVariationTitle("");
    setMoveContent("");
    setIsVariationComment(false);

    // Notify parent that variation was commented (for cleanup)
    if (isVariationComment) {
      onVariationCommented();
    }
  };

  return (
    <div className="" style={{ width: '', display: 'flex', flexDirection: 'column', alignItems: 'start', marginTop: '', marginBottom: '5px', gap: '3px' }}>
      {/* Show moves from user if commenting on a variation */}
      {isVariationComment ? (
        <div style={{
          border: '2px solid #3066be',
          borderRadius: '5px',
          minHeight: '150px',
          padding: '10px',
          marginBottom: '10px',
          background: 'rgba(48, 102, 190, 0.1)',
          width: '500px'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: 'gold',
            textAlign: 'center'
          }}>
            Your Move Variation:
          </div>
          <Moves
            moves={variationToComment || moves}
            currentMove={currentMoveIdx}
            setCurrentMove={setCurrentMoveIdx}
            starredMoves={[]}
            type={"COMMENTS"}
            userId={-1}
            key={`variation-moves-${JSON.stringify(variationToComment || moves).length}`}
            variationToComment={variationToComment}
          />
        </div>
      ) : (
        <div>
          <Moves
            moves={moves}
            currentMove={currentMoveIdx}
            setCurrentMove={setCurrentMoveIdx}
            starredMoves={[]}
            type={"COMMENT_FULL"}
            userId={-1}
            key={`regular-moves-${JSON.stringify(moves).length}`}
          />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '7px' }}>
        {/* Title field for variation comments - restructured into a single row */}
        {isVariationComment && (
          <div style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px'
          }}>
            {/* Variation control icons */}
            <div style={{
              display: 'flex',
              gap: '15px',
              marginRight: '5px'
            }}>
              {/* Info tooltip */}
              {/* <div
                style={{
                  color: '#3066be',
                  fontSize: '18px',
                  cursor: 'help',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Variations allow you to analyze alternative moves and save them with comments. Delete a variation to remove it, undo to remove the last move."
              >
                <i className="fa-solid fa-circle-info"></i>
              </div> */}

              {/* Trash icon (Delete Variation) */}
              <div
                onClick={handleDeleteCurrentCommentLine}
                style={{
                  color: '#ff3366',
                  fontSize: '22px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  marginLeft: '10px'
                }}
                title="Delete this variation"
                onMouseOver={(e) => {
                  e.currentTarget.style.color = '#ff6699';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#ff3366';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className="fa-solid fa-trash"></i>
              </div>

              {/* Undo icon (Undo Move) */}
              <div
                onClick={handleUndoCommentMove}
                style={{
                  color: '#3066be',
                  fontSize: '22px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                title="Undo last move"
                onMouseOver={(e) => {
                  e.currentTarget.style.color = '#4488ee';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#3066be';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className="fa-solid fa-scissors"></i>
              </div>
            </div>

            <input
              type="text"
              className={clsx("", error && "input-error")}
              value={variationTitle}
              onChange={(e) => {
                setError(false);
                setVariationTitle(e.target.value);
              }}
              onFocus={() => {
                setIsInputFocused(true);
              }}
              onBlur={() => {
                setIsInputFocused(false);
              }}
              style={{
                width: "310px",
                height: "40px",
                paddingLeft: '20px',
                fontSize: '18px',
                borderRadius: '5px',
                background: 'transparent',
                color: 'white',
                border: error ? '3px solid red' : '3px solid #58b047',
                outline: error ? 'none' : '',
              }}
              placeholder={"Caption your variation..."}
            />

            {/* <button
              style={{ 
                height: '48px', 
                backgroundColor: '#333', 
                color: 'white', 
                borderRadius: '5px', 
                border: '2px solid #666', 
                fontSize: '16px',
                padding: '0 15px',
                marginRight: '10px',
                cursor: 'pointer'
              }}
              onClick={() => {
                setIsVariationComment(false);
                setVariationTitle("");
                onVariationCommented(); // Notify parent to clean up
              }}
            > 
              CANCEL
            </button> */}

            <button
              className=""
              style={{
                height: '48px',
                minWidth: '95px',
                backgroundColor: 'black',
                color: '#58b047',
                borderRadius: '5px',
                border: '2px solid #58b047',
                fontSize: '20px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
              onClick={submitComment}
            >
              SEND
            </button>
          </div>
        )}

        {/* Regular comment field if not a variation comment */}
        {!isVariationComment && (
          <div style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: '10px'
          }}>
            <input
              type="text"
              className={clsx("", error && "input-error")}
              value={comment}
              onChange={(e) => {
                setError(false);
                setComment(e.target.value);
              }}
              onFocus={() => {
                setIsInputFocused(true);
              }}
              onBlur={() => {
                setIsInputFocused(false);
              }}
              style={{
                flex: '1',
                height: "40px",
                paddingLeft: '20px',
                fontSize: '18px',
                borderRadius: '5px',
                background: '',
                color: 'black',
                border: error ? '3px solid red' : '3px solid #58b047',
                outline: error ? 'none' : ''
              }}
              placeholder={error ? "YOU MUST TYPE SOMETHING" : "Start typing a comment..."}
            />

            <button
              className=""
              style={{
                height: '48px',
                minWidth: '95px',
                backgroundColor: 'black',
                color: '#58b047',
                borderRadius: '5px',
                border: '2px solid #58b047',
                fontSize: '20px',
                fontWeight: 'bold',
                marginLeft: '5px',
                cursor: 'pointer'
              }}
              onClick={submitComment}
            >
              SEND
            </button>
          </div>
        )}

        {/* Removed the separate buttons container that was previously here */}
      </div>
    </div>
  );
};

export default AddComment;
